import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { IMAGES } from "../../data/content";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.heroBg}
          alt="AI software architecture"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/70 via-[#020617]/85 to-[#020617]" />
        <div className="absolute inset-0 grid-pattern opacity-40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-8 backdrop-blur-sm"
          >
            <span className="h-2 w-2 rounded-full bg-[#06B6D4] animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">
              AI-First Software Product Studio
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]"
          >
            Intelligent Software,
            <br />
            Engineered for <span className="gradient-text">Impact.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-lg text-[#94A3B8] leading-relaxed max-w-2xl"
          >
            Rishmathi Tech Ventures builds AI-driven, enterprise-grade software
            products that solve complex operational challenges with unmatched
            speed and cost-efficiency.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollTo("#products")}
              className="group inline-flex items-center justify-center gap-2 bg-[#6366F1] text-white hover:bg-[#4F46E5] transition-all rounded-full px-8 py-4 font-medium shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)]"
              data-testid="hero-explore-products"
            >
              Explore Our Products
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollTo("#about")}
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/20 hover:border-[#06B6D4] hover:text-[#06B6D4] transition-all rounded-full px-8 py-4 font-medium"
              data-testid="hero-watch-how"
            >
              <Play size={16} />
              Watch How We Build
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              { k: "23+", v: "Years Architectural Experience" },
              { k: "AI-First", v: "Internal Frameworks" },
              { k: "Enterprise", v: "Grade Security" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-heading text-2xl font-bold gradient-text">
                  {s.k}
                </div>
                <div className="text-sm text-[#64748B]">{s.v}</div>
              </div>
            ))}
          </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35 }}
            className="hidden lg:flex lg:col-span-5 relative items-center justify-center"
            data-testid="hero-logo-face"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.30),transparent_62%)] blur-2xl" />
            <motion.img
              src="/rishmathi-mark-white.png"
              alt="Rishmathi AI mark"
              className="relative w-[400px] max-w-full drop-shadow-[0_0_45px_rgba(6,182,212,0.35)]"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
